import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FarmData, FarmInfrastructures } from 'types/FarmData';
import { GoogleMap } from 'ui';
import { publicPath } from 'enums/publicRoutes';
import { LatLng, centroid, haversineKm, parsePoint } from 'utils/geoHelpers';
import { useUserLocation } from 'utils/useUserLocation';
import { ProducerPill } from './ProducerPill';

const SINGLE_PIN_ZOOM = 11;
const MIN_ZOOM = 6;
const MAP_BOX = { width: 358, height: 302 };
const FIT_PADDING = 1.6;
const LAT_STRETCH = 1.84;

const zoomToFit = (points: LatLng[]): number => {
  if (points.length < 2) return SINGLE_PIN_ZOOM;

  const lats = points.map((p) => p.lat);
  const lngs = points.map((p) => p.lng);
  const lngSpan = Math.max(...lngs) - Math.min(...lngs);
  const latSpan = (Math.max(...lats) - Math.min(...lats)) * LAT_STRETCH;
  if (lngSpan === 0 && latSpan === 0) return SINGLE_PIN_ZOOM;

  const zoomFor = (span: number, px: number) =>
    Math.log2((px * 360) / (256 * span * FIT_PADDING));

  const zoom = Math.floor(
    Math.min(
      lngSpan ? zoomFor(lngSpan, MAP_BOX.width) : SINGLE_PIN_ZOOM,
      latSpan ? zoomFor(latSpan, MAP_BOX.height) : SINGLE_PIN_ZOOM
    )
  );

  return Math.min(SINGLE_PIN_ZOOM, Math.max(MIN_ZOOM, zoom));
};

interface FarmLocationCardProps {
  farm?: FarmData;
  infrastructures?: FarmInfrastructures[];
  producerName?: string | null;
  isEmarket: boolean;
}

interface MapPin {
  id: number;
  position: LatLng;
}

const FarmLocationCard = ({
  farm,
  infrastructures,
  producerName,
  isEmarket,
}: FarmLocationCardProps) => {
  const intl = useIntl();
  const { location, status, requestLocation } = useUserLocation();

  const farmName = farm?.brand ?? farm?.name;
  const farmPath = isEmarket
    ? publicPath.farm(farm?.slug, farm?.id)
    : publicPath.wholesaleFarm(farm?.slug, farm?.id);

  const marketFlag = isEmarket
    ? 'farm_infrastructures_products.emarket'
    : 'farm_infrastructures_products.wholesale';

  const pins = useMemo<MapPin[]>(
    () =>
      (infrastructures ?? [])
        .filter((infra) => infra.show_on_map !== false)
        .filter((infra) => infra.pivot?.[marketFlag] !== false)
        .map((infra) => ({
          id: infra.id,
          position: parsePoint(infra.maps_api_point),
        }))
        .filter((pin): pin is MapPin => !!pin.position),
    [infrastructures, marketFlag]
  );

  const nearestPin = useMemo(() => {
    if (!pins.length) return null;
    if (!location) return pins[0];

    return pins.reduce((nearest, pin) =>
      haversineKm(location, pin.position) <
      haversineKm(location, nearest.position)
        ? pin
        : nearest
    );
  }, [pins, location]);

  const distanceKm =
    location && nearestPin
      ? Math.round(haversineKm(location, nearestPin.position))
      : null;

  const destination = nearestPin
    ? `${nearestPin.position.lat},${nearestPin.position.lng}`
    : (farm?.actual_full_address ?? farm?.registered_full_address ?? '');

  const googleUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    destination
  )}`;
  const wazeUrl = nearestPin
    ? `https://waze.com/ul?ll=${encodeURIComponent(destination)}&z=10`
    : `https://waze.com/ul?navigate=yes&q=${encodeURIComponent(destination)}`;

  if (!farmName && !pins.length) return null;

  return (
    <aside className={'farm-location'}>
      {!!pins.length && (
        <div className={'farm-location-map'}>
          <GoogleMap
            features={pins}
            center={centroid(pins.map((pin) => pin.position)) ?? undefined}
            zoom={zoomToFit(pins.map((pin) => pin.position))}
          />
        </div>
      )}
      <div className={'farm-location-body'}>
        {!!farmName && (
          <ProducerPill
            label={
              producerName
                ? 'product_view.distributor_with_name'
                : 'product_view.producer_with_name'
            }
            name={farmName}
            to={farm?.id ? farmPath : undefined}
            icon={producerName ? 'distributor-icon' : 'location-pin-icon'}
          />
        )}

        {!!pins.length &&
          (distanceKm !== null ? (
            <p className={'farm-location-distance'}>
              {intl.formatMessage(
                { id: 'product_view.distance_from_you' },
                { distance: distanceKm }
              )}
            </p>
          ) : (
            <p className={'farm-location-hint'}>
              {status === 'denied' || status === 'unsupported'
                ? intl.formatMessage({ id: 'product_view.location_denied' })
                : intl.formatMessage({
                    id: 'product_view.enable_location_hint',
                  })}{' '}
              {status !== 'unsupported' && (
                <button
                  type={'button'}
                  className={'farm-location-enable'}
                  onClick={requestLocation}
                  disabled={status === 'requesting'}
                >
                  {intl.formatMessage({ id: 'product_view.enable_location' })}
                </button>
              )}
            </p>
          ))}

        {!!destination && (
          <div className={'farm-location-actions'}>
            <a href={googleUrl} target={'_blank'} rel={'noreferrer'}>
              {intl.formatMessage({ id: 'farms.google_map' })}
            </a>
            <a href={wazeUrl} target={'_blank'} rel={'noreferrer'}>
              {intl.formatMessage({ id: 'farms.waze' })}
            </a>
          </div>
        )}
      </div>
    </aside>
  );
};

export default FarmLocationCard;
